import { UploadOutlined } from "@ant-design/icons";
import { FormInstance, UploadFile } from "antd";
import {
  TimeSlotsImportResponse,
  useCreateImportTimeslotsMutation,
  useGetImportTimeslotsTemplate,
} from "api/mutations/fileImport";
import DateRenderer from "components/DateRenderer";
import { FileImportModes } from "components/FileImport/helpers";
import { Button, Drawer, Form, Row, Space, Upload } from "components/styleguide";
import {
  downloadBinaryFile,
  DownloadFileData,
  extractErrorFile,
  extractTemplateFile,
} from "pages/ConferenceTimings/helpers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { handleFileFakeRequest } from "utils/documentUtils";
import { v4 as uuidv4 } from "uuid";

export default function ImportDrawer({
  forumId,
  title,
  visible,
  onClose,
  onImported,
  form,
}: {
  forumId: number;
  title: string;
  visible: boolean;
  onClose: () => void;
  onImported: () => void;
  form: FormInstance;
}) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(null);
  const [uploadedBy, setUploadedBy] = useState<string | undefined>(undefined);
  const [uploadedAt, setUploadedAt] = useState<string | undefined>(undefined);
  const [errorFile, setErrorFile] = useState<DownloadFileData | null>(null);

  const createMutation = useCreateImportTimeslotsMutation();

  const getTemplateQuery = useGetImportTimeslotsTemplate(
    {},
    {
      enabled: false,
      refetchOnWindowFocus: false,
      onSuccess: ({ data }) => {
        toast.success(t("Template downloaded successfully"), { autoClose: 30000 });
        if (data && data.templateFile && data.templateContentType && data.templateName) {
          const templateData = extractTemplateFile(data);
          if (templateData) {
            downloadBinaryFile(templateData);
          }
        } else {
          toast.error(t("Failed to download template"), { autoClose: 30000 });
        }
      },
      onError: () => {
        toast.error(t("Something went wrong while downloading the template"), { autoClose: 30000 });
      },
    },
  );

  const isLoading = createMutation.isLoading;

  const handleResponse = (data: TimeSlotsImportResponse) => {
    const success = data.isSuccess ?? false;
    const successRowsCount = data.successItemsCount ?? 0;
    const failedRowsCount = data.failedItemsCount ?? 0;

    setUploadedAt(data.uploadedAt);
    setUploadedBy(data.uploadedBy);

    if (!success) {
      toast.error(
        t("Timeslot import validation failed for {{count}} row(s). Please download the error file below.", {
          count: failedRowsCount,
        }),
        { autoClose: 30000 },
      );

      if (data.errorFile) {
        setErrorFile(extractErrorFile(data.errorFile as Parameters<typeof extractErrorFile>[0]));
      } else {
        toast.error(
          t(
            "Failed to write errors in the file. Please verify if source file is a valid file (without 'Error' column).",
          ),
          { autoClose: 30000 },
        );
      }

      return false;
    }

    toast.success(t("Timeslot import completed successfully for {{count}} row(s).", { count: successRowsCount }), {
      autoClose: 30000,
    });
    setErrorFile(null);
    return true;
  };

  const clearAllData = () => {
    setSelectedFile(null);
    setUploadedAt(undefined);
    setUploadedBy(undefined);
    setErrorFile(null);
  };

  const handleClear = () => {
    clearAllData();
    form.resetFields();
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  const handleUpload = () => {
    if (!selectedFile?.originFileObj) {
      toast.info(t("Select file first"), { autoClose: 10000 });
      return;
    }

    setErrorFile(null);

    createMutation.mutate(
      {
        forumId,
        formFile: selectedFile.originFileObj,
        importMode: FileImportModes.Upsert,
        importKey: uuidv4(),
      },
      {
        onSuccess: ({ data }) => {
          const success = handleResponse(data);
          if (success) {
            onImported();
            handleClear();
          }
        },
        onError: () => {
          toast.error(t("Something went wrong"));
        },
      },
    );
  };

  const downloadErrorFile = () => {
    if (!errorFile) return;

    downloadBinaryFile(errorFile);
  };

  const handleDownloadTemplate = async () => {
    await getTemplateQuery.refetch();
  };

  return (
    <Drawer
      title={t(title)}
      open={visible}
      onClose={handleClose}
      contentWrapperStyle={{ minWidth: "50%" }}
      destroyOnClose
    >
      <h3 style={{ marginBottom: 20 }}>{t("This import is for creating new timeslots and timeslot sessions only.")}</h3>

      <Form form={form} onFinish={handleUpload}>
        <Form.Item
          name="formFile"
          rules={[
            {
              required: true,
              message: t("errors.required", { prop: "$t(File)" }),
            },
          ]}
        >
          <Upload.Dragger
            maxCount={1}
            accept=".xlsx,.xls,.xlsm"
            beforeUpload={clearAllData}
            onRemove={clearAllData}
            customRequest={handleFileFakeRequest}
            onChange={(file) => setSelectedFile(file.file)}
          >
            <h1>
              <UploadOutlined />
            </h1>
            <p>{t("Drag file here or Browse")}</p>
            <p>
              <strong>{t("Allowed formats")}:</strong> .xlsx, .xls, .xlsm
            </p>
          </Upload.Dragger>
        </Form.Item>

        {uploadedAt && uploadedBy && (
          <Space direction="vertical" style={{ marginBottom: 20 }}>
            <Row>
              <strong>{t("Uploaded By")}</strong>: {uploadedBy}
            </Row>
            <Row>
              <strong>{t("Uploaded At")}</strong>: <DateRenderer date={uploadedAt} showTime />
            </Row>
          </Space>
        )}

        <Row justify="space-between">
          <Row>
            <Button onClick={handleClear} loading={isLoading}>
              {t("Clear")}
            </Button>
            <Button style={{ marginLeft: 10 }} onClick={handleDownloadTemplate} loading={getTemplateQuery.isFetching}>
              {t("Download Import Template")}
            </Button>
          </Row>
          <Row>
            {errorFile && (
              <Button onClick={downloadErrorFile} style={{ marginRight: 10, border: "1px solid orange" }}>
                {t("Download Import Errors")}
              </Button>
            )}
            <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
              {t("Upload")}
            </Button>
          </Row>
        </Row>
      </Form>
    </Drawer>
  );
}
